<template>
  <div>
    <global-confirm-box ref="confirmBox" />

    <div class="d-flex align-center justify-space-between flex-wrap mb-2">
      <v-tabs v-model="activeTab" class="promotion-tabs">
        <v-tab v-for="tab in tabs" :key="tab.value">
          {{ tab.label }}
          <v-chip x-small class="ml-2" :color="tab.badgeColor" text-color="white">
            {{ tab.count }}
          </v-chip>
        </v-tab>
      </v-tabs>
      <primary-button v-if="isAdmin" class="ml-auto" @click="openAddDialog">
        <v-icon left small>mdi-plus</v-icon>
        {{ $t("finance_new_campaign") }}
      </primary-button>
    </div>

    <v-card outlined>
      <v-data-table
        :headers="headers"
        :items="filteredPromotions"
        :loading="loading"
        :loading-text="$t('finance_loading')"
        :no-data-text="$t('finance_no_campaigns_in_category')"
        mobile-breakpoint="0"
        @click:row="openPromotion"
      >
        <template #item.name="{ item }">
          <span class="font-weight-bold">{{ item.name }}</span>
        </template>
        <template #item.period="{ item }">
          {{ formatDate(item.periodStart) }} - {{ formatDate(item.periodEnd) }}
        </template>
        <template #item.fundedBy="{ item }">
          {{ $t(PROMOTION_FUNDING_META[item.fundedBy]) }}
        </template>
        <template #item.orderCount="{ item }">
          {{ item.orderCount }}
        </template>
      </v-data-table>
    </v-card>

    <promotion-detail-dialog v-model="detailOpen" :promotion="selectedPromotion" />

    <promotion-form-dialog
      v-if="isAdmin"
      v-model="dialogOpen"
      :saving="saving"
      @save="onSavePromotion"
    />
  </div>
</template>

<script>
import restaurantFinanceService from "@/services/restaurantFinance";
import hasSnackbar from "@/mixins/hasSnackbar";
import PromotionDetailDialog from "./dialogs/PromotionDetailDialog.vue";
import PromotionFormDialog from "./dialogs/PromotionFormDialog.vue";
import { PROMOTION_FUNDING_META, formatDate } from "./constants";

const mapPromotionDto = (dto) => ({
  id: dto.id,
  name: dto.name,
  periodStart: dto.period_start,
  periodEnd: dto.period_end,
  status: dto.status,
  discount: dto.discount,
  fundedBy: dto.funded_by,
  orderaShare: dto.ordera_share,
  restaurantShare: dto.restaurant_share,
  orderCount: dto.order_count,
  newSales: dto.new_sales,
});

export default {
  name: "FinanceTabPromotions",

  components: { PromotionDetailDialog, PromotionFormDialog },

  mixins: [hasSnackbar],

  data() {
    return {
      PROMOTION_FUNDING_META,
      promotions: [],
      loading: false,
      saving: false,
      activeTab: 0,
      detailOpen: false,
      selectedPromotion: null,
      dialogOpen: false,
    };
  },

  computed: {
    restaurantId() {
      return this.$store.state.restaurant.id;
    },
    isAdmin() {
      return this.$store.state.user?.user?.type === "admin";
    },
    headers() {
      return [
        { text: this.$t("finance_campaign"), value: "name" },
        { text: this.$t("finance_period_label"), value: "period", sortable: false },
        { text: this.$t("finance_discount"), value: "discount" },
        { text: this.$t("finance_funding"), value: "fundedBy" },
        { text: this.$t("finance_order_count"), value: "orderCount", align: "end" },
      ];
    },
    tabs() {
      const statuses = [
        { value: "active", label: this.$t("finance_tab_active"), badgeColor: "success" },
        { value: "completed", label: this.$t("finance_tab_completed"), badgeColor: "primary" },
        { value: "planned", label: this.$t("finance_tab_planned"), badgeColor: "warning" },
      ];
      return statuses.map((s) => ({
        ...s,
        count: this.promotions.filter((p) => p.status === s.value).length,
      }));
    },
    filteredPromotions() {
      const status = this.tabs[this.activeTab] ? this.tabs[this.activeTab].value : "active";
      return this.promotions.filter((p) => p.status === status);
    },
  },

  created() {
    this.refresh();
  },

  methods: {
    formatDate,

    async refresh() {
      if (!this.restaurantId) return;
      this.loading = true;
      try {
        const { data } = await restaurantFinanceService.getPromotions(this.restaurantId);
        this.promotions = (data?.data || []).map(mapPromotionDto);
      } catch (error) {
        this.showErrorSnackbar(this.$t("finance_promotions_load_error"));
      } finally {
        this.loading = false;
      }
    },

    openPromotion(promotion) {
      this.selectedPromotion = promotion;
      this.detailOpen = true;
    },

    openAddDialog() {
      this.dialogOpen = true;
    },

    async onSavePromotion(payload) {
      this.saving = true;
      try {
        const { data } = await restaurantFinanceService.createPromotion(
          this.restaurantId,
          payload
        );
        const created = mapPromotionDto(data.data || data);
        this.promotions = [created, ...this.promotions];
        this.showSuccessSnackbar(this.$t("finance_campaign_created"));
        this.dialogOpen = false;
      } catch (error) {
        this.showErrorSnackbar(this.$t("finance_campaign_create_error"));
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.promotion-tabs {
  flex: 1 1 auto;
}
</style>
